import { useState } from "react";

/**
 * The moment a grown-up says yes.
 *
 * This is the only thing in the app a child cannot earn by themselves — every
 * star is their own doing, and this one is somebody choosing to honour it. It
 * gets the biggest thing on screen for a few seconds because that difference is
 * the whole point of the goal loop.
 *
 * It does not dismiss itself. A child who put the tablet down mid-approval
 * comes back to the news still waiting for them, which is the case this whole
 * feature exists for.
 */

/** Confetti, laid out deterministically so it never shimmers on re-render. */
const FLECKS = Array.from({ length: 26 }, (_, i) => ({
  left: (i * 37) % 100,
  delay: (i % 7) * 0.18,
  drift: ((i % 5) - 2) * 16,
  color: ["var(--color-gold)", "var(--color-pink)", "var(--color-cyan)", "var(--color-mint)", "var(--color-lilac)"][i % 5],
}));

export function Approved({
  label,
  emoji,
  onDone,
}: {
  label: string;
  emoji: string;
  onDone: () => void;
}) {
  const [closing, setClosing] = useState(false);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-deep/92 px-6"
      role="alertdialog"
      aria-modal="true"
      aria-label={`Your grown-up said yes to ${label}`}
    >
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        {FLECKS.map((fleck, i) => (
          <span
            key={i}
            className="fleck absolute top-[-20px] h-[10px] w-[10px] rounded-[2px]"
            style={{
              left: `${fleck.left}%`,
              background: fleck.color,
              animationDelay: `${fleck.delay}s`,
              ["--drift" as string]: `${fleck.drift}px`,
            }}
          />
        ))}
      </div>

      <div className="pop relative text-center">
        <div className="approved-seal text-[86px] leading-none">{emoji}</div>
        <p className="disp mt-4 text-[30px] leading-tight text-gold">Your grown-up said yes</p>
        <p className="disp mt-1 text-[24px] text-cream">{label}</p>
        <p className="mx-auto mt-3 max-w-[26rem] text-[16px] leading-relaxed text-cream/65">
          They'll sort it out with you in person. Your stardust has been spent.
        </p>

        <button
          onClick={() => {
            setClosing(true);
            onDone();
          }}
          disabled={closing}
          className="kidbtn disp mt-7 rounded-full bg-gold px-8 py-4 text-[21px] text-night shadow-[0_6px_0_rgba(0,0,0,.35)]"
        >
          Yes!
        </button>
      </div>
    </div>
  );
}
